
import { Component, Vue } from 'vue-property-decorator'
import { AppModule } from '@/store/module'
import FooterBlock from '@/components/FooterBlock.vue'
import ContactBlock from '@/components/ContactBlock.vue'
import QuestionBlock from '@/components/QuestionBlock.vue'
import HelpBlock from '@/components/HelpBlock.vue'
import PageFaqSection from '@/components/PageFaqSection.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import faqData from '@/data/faq.json'
import { FAQ_SCENES, FaqData, PageFaqGroup } from '@/types/faq'
import { filterFaqGroupsByScene, toPageFaqGroups } from '@/utils/faq'

import Banner from './components/Banner.vue'

import { scrollToElem } from '@/utils/scroll'

@Component({
  components: {
    Banner,
    FooterBlock,
    ContactBlock,
    QuestionBlock,
    HelpBlock,
    PageFaqSection,
    Breadcrumb,
  },
})
export default class Sale extends Vue {
  get isGoogleWallet(): boolean { return this.$route.name === 'googleWallet' }
  readonly googleWalletFaqGroups = toPageFaqGroups(filterFaqGroupsByScene(faqData as FaqData, FAQ_SCENES.GOOGLE_WALLET))
  get isShareCoupons(): boolean { return this.$route.name === 'shareCoupons' }

  readonly shareCouponsFaqGroups = toPageFaqGroups(filterFaqGroupsByScene(faqData as FaqData, FAQ_SCENES.SHARE_COUPONS))

  showContact = true

  readonly restaurantSocialMgmFaqGroups: readonly PageFaqGroup[] = toPageFaqGroups(
    filterFaqGroupsByScene(
      faqData as unknown as FaqData,
      FAQ_SCENES.RESTAURANT_SOCIAL_MGM,
    ),
  )

  breadcrumbMap: Record<string, { label: string; to?: string }[]> = {
    googleWallet: [{ label: '首頁', to: '/' }, { label: '最新功能' }, { label: 'Google Wallet 推廣' }],
    deliveryPlatform: [{ label: '首頁', to: '/' }, { label: '最新功能' }, { label: '外賣平台整合' }],
    member: [{ label: '首頁', to: '/' }, { label: '最新功能' }, { label: '會員系統' }],
    discount: [{ label: '首頁', to: '/' }, { label: '最新功能' }, { label: '優惠功能' }],
    bigScreen: [{ label: '首頁', to: '/' }, { label: '最新功能' }, { label: '取餐大屏幕' }],
    miniProgram: [{ label: '首頁', to: '/' }, { label: '最新功能' }, { label: '餐飲小程式' }],
    newUi: [{ label: '首頁', to: '/' }, { label: '最新功能' }, { label: '新點餐界面' }],
    kiosk: [{ label: '首頁', to: '/' }, { label: '最新功能' }, { label: '自助點餐Kiosk' }],
    shareCoupons: [{ label: '首頁', to: '/' }, { label: '最新功能' }, { label: '開放領券' }],
    restaurantSocialMgm: [{ label: '首頁', to: '/' }, { label: '最新功能' }, { label: '餐廳社交MGM工具' }],
  }

  get breadcrumbItems() {
    return this.breadcrumbMap[this.$route.name as string] || []
  }

  bannerMap = {
    googleWallet: {
      imgPc: require('@/assets/sale/google-wallet-pc.jpg'),
      imgH5: require('@/assets/sale/google-wallet-h5-clean.png'),
      alt: 'Google Wallet 推廣｜PinMe 電子會員卡與優惠',
      title: '會員卡搬入\nGoogle Wallet\n客人行過都會想起你',
      mobileCopyAlign: 'center',
      subtitle: '一個掣，你張會員卡就入咗客人部手機。積分自動更新、優惠自動彈出、行過你間店 Google Wallet 自己開聲—由今日開始，你嘅電子會員卡唔會再瞓覺。',
      copyColor: '#ffffff',
      bullets: [
        '一撥即存—會員卡直入 Google Wallet，唔使下載 App',
        '優惠憑證同步上 Wallet，客人未入店已經睇到',
        '客人行近門店，Google Wallet 自動彈卡提醒',
        '保存率、核銷數、點擊數——中台實時睇晒',
      ],
    },
    shareCoupons: {
      imgPc: require('@/assets/sale/coupon-pc.png'),
      imgH5: require('@/assets/sale/coupon-h5-clean.png'),
      alt: 'PinMe 開放領券',
    },
    'deliveryPlatform': {
      imgH5: require('@/assets/sale/delivery-platform-h5-clean.png'),
      imgPc: require('@/assets/sale/delivery-platform-pc.jpg'),
      alt: '一部POS管理所有外賣平臺訂單',
      title: '一部POS\n管理所有外賣平台訂單',
      subtitle: '已對接Foodpanda、Deliveroo',
    },
    restaurantSocialMgm: {
      imgPc: require('@/assets/sale/mgm-pc.png'),
      imgH5: require('@/assets/sale/mgm-h5-clean.png'),
      alt: 'PinMe 餐廳社交分享與雙向獎勵功能',
    },
    member: {
      imgH5: require('@/assets/sale/member-h5-clean.png'),
      imgPc: require('@/assets/sale/member-pc.jpg'),
      alt: '強大會員系統提升顧客的忠誠度',
      title: '強大會員系統\n提升顧客的忠誠度',
      subtitle: '是餐廳成功的關鍵所在',
    },
    discount: {
      imgH5: require('@/assets/sale/discount-h5-clean.png'),
      imgPc: require('@/assets/sale/discount-pc.jpg'),
      alt: '強大的營銷功能，幫助餐廳提升營業額',
      title: '幫助餐廳\n提升營業額',
      subtitle: '簡單的設置，輕鬆擁有「全自動化」的餐廳營銷體系',
    },
    bigScreen: {
      imgH5: require('@/assets/sale/big-screen-h5-clean.png'),
      imgPc: require('@/assets/sale/big-screen-pc.jpg'),
      alt: '取餐大屏幕，餐品訂單製作狀態，一目了然',
      title: '取餐大屏幕',
      subtitle: '餐品訂單製作狀態，一目了然',
      copyColor: '#000000',
      mobileCopyAlign: 'center',
      h5ClassName: 'big-screen-h5',
    },
    miniProgram: {
      imgH5: require('@/assets/sale/mini-program-h5-clean.png'),
      imgPc: require('@/assets/sale/mini-program-pc.jpg'),
      alt: '免費升級「餐飲小程式」',
      title: '免費升級',
      subtitle: '餐飲小程式',
      copyAlign: 'center',
      copyColor: '#254062',
      pcClassName: 'mini-program-pc',
      h5ClassName: 'mini-program-h5',
    },
    newUi: {
      imgH5: require('@/assets/sale/new-ui-h5-clean.png'),
      imgPc: require('@/assets/sale/new-ui-pc-clean.png'),
      alt: '"煥然一新"點餐手機界面',
      title: '「煥然一新」\n點餐手機界面',
      subtitle: '你的品牌你做主！「隨心定製」、「更酷」、「更強大」',
      copyAlign: 'center',
      copyColor: '#ffffff',
      pcClassName: 'new-ui-pc',
      h5ClassName: 'new-ui-h5'
    },
    kiosk: {
      imgH5: require('@/assets/sale/kiosk-h5-clean.png'),
      imgPc: require('@/assets/sale/kiosk-pc-clean.png'),
      alt: '自助點餐Kiosk',
      title: 'PinMe 餐飲「自助點餐終端」KIOSK',
      subtitle: '月月慳超過二萬蚊，一部機就搞掂！',
      copySide: 'left',
      mobileCopyAlign: 'left',
      copyAlign: 'center',
      copyColor: '#ffffff',
      pcClassName: 'kiosk-pc',
      h5ClassName: 'kiosk-h5'
    }
  }

  get bannerData() {
    return this.bannerMap[this.$route.name]
  }

  get isRestaurantSocialMgm(): boolean {
    return this.$route.name === 'restaurantSocialMgm'
  }

  get isH5() {
    return AppModule.isH5
  }

  contactHeight = 0
  footerHeight = 0
  screenHeight = 0
  bodyHeight = 0

  mounted() {
    this.contactHeight = (this.$refs.contact as Vue & { $el: HTMLElement }).$el.offsetHeight
    this.footerHeight = (this.$refs.footer as Vue & { $el: HTMLElement }).$el.offsetHeight
    this.screenHeight = document.documentElement.clientHeight
    window.addEventListener('scroll', this.scrollHandle)
  }

  beforeDestroy() {
    window.removeEventListener('scroll', this.scrollHandle)
  }

  scrollHandle() {
    const scrollTop = document.documentElement.scrollTop
    if (!this.bodyHeight) {
      this.bodyHeight = document.body.offsetHeight
    }
    if (scrollTop >= this.bodyHeight - this.screenHeight - this.footerHeight - this.contactHeight) {
      this.showContact = false
    } else {
      this.showContact = true
    }
  }

  scrollToContact() {
    scrollToElem('#contact', 500, 80)
  }
}
